import { Injectable } from '@angular/core';
import { SupabaseService } from '../supabase/supabase.service';
import { AuthenticationService } from '../authentication/authentication.service';

export interface FileUploadResponse {
  id: string;
  path: string;
  size: number;
  type: string;
  status: string;
  error_message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private readonly _BUCKET_NAME = 'documents';

  constructor(
    private readonly _supabaseService: SupabaseService,
    private readonly _authService: AuthenticationService
  ) { }

  async uploadFile(file: File, metadata: { name: string; note: string }): Promise<FileUploadResponse> {
    const userId = this._getUserId();
    const supabase = await this._supabaseService.getClient();
    const fileName = `${userId}/${Date.now()}-${encodeURIComponent(metadata.name.replace(/\s+/g, '-'))}`;

    const { data, error } = await supabase.storage
      .from(this._BUCKET_NAME)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    const { error: insertError } = await supabase
      .from('documents')
      .insert({
        user_id: userId,
        file_name: metadata.name,
        storage_path: data.path
      });

    if (insertError) throw insertError;

    return {
      id: data.path,
      path: data.path,
      size: file.size,
      type: file.type,
      status: 'pending',
      error_message: undefined
    };
  }

  async deleteFile(fileId: string): Promise<void> {
    const userId = this._getUserId();
    const supabase = await this._supabaseService.getClient();

    const { error: storageError } = await supabase.storage
      .from(this._BUCKET_NAME)
      .remove([fileId]);

    if (storageError) throw storageError;

    const { error: dbError } = await supabase
      .from('documents')
      .delete()
      .eq('user_id', userId)
      .eq('storage_path', fileId);

    if (dbError) throw dbError;
  }

  async getFiles(): Promise<FileUploadResponse[]> {
    const userId = this._getUserId();
    const supabase = await this._supabaseService.getClient();

    const [documentsResult, storageResult] = await Promise.all([
      supabase.from('documents').select('file_name, storage_path, status, error_message').eq('user_id', userId),
      supabase.storage.from(this._BUCKET_NAME).list(userId)
    ]);

    if (documentsResult.error) throw documentsResult.error;
    if (storageResult.error) throw storageResult.error;

    const storageMap = new Map(storageResult.data?.map(file => [`${userId}/${file.name}`, file]) || []);

    return (documentsResult.data || []).map(doc => {
      const storageFile = storageMap.get(doc.storage_path);
      return {
        id: doc.storage_path,
        path: doc.storage_path,
        size: storageFile?.metadata?.['size'] || 0,
        type: storageFile?.metadata?.['mimetype'] || 'application/pdf',
        status: doc.status || 'pending',
        error_message: doc.error_message
      };
    });
  }

  async downloadFile(filePath: string): Promise<Blob> {
    const supabase = await this._supabaseService.getClient();

    const { data, error } = await supabase.storage
      .from(this._BUCKET_NAME)
      .download(filePath);

    if (error) throw error;
    if (!data) throw new Error('No data received');

    return data;
  }

  private _getUserId(): string {
    const userDetail: any = this._authService.getUserDetail();
    const userId = userDetail?.user?.id || userDetail?.id;

    if (!userId) {
      throw new Error('No active session');
    }

    return userId;
  }
}
